import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SavedFiltersDropdown,
} from '../components/ui/enhanced-table';
import { useTableSort, useSavedFilters } from '../hooks/useTableEnhancements';
import { Bell, Play, Clock, AlertTriangle, CheckCircle, BarChart3, Send, Activity, Zap, XCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const NOTIFICATION_TYPES = ['daily_summary', 'overdue_alert'];
const DELIVERY_STATUSES = ['pending', 'sent', 'failed'];

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fBranch, setFBranch] = useState('all');
  const [fType, setFType] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [generating, setGenerating] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [deliveryAttempts, setDeliveryAttempts] = useState({});
  const [delivering, setDelivering] = useState('');

  // Sorting with persistence
  const { sortField, sortDir, toggleSort, sortData } = useTableSort('notifications_page', 'created_at', 'desc');

  // Saved filters
  const { savedFilters, saveCurrentFilters, saveFilterPreset, deleteFilterPreset, loadCurrentFilters } = useSavedFilters('notif_page');

  // Load saved filters on mount
  useEffect(() => {
    const saved = loadCurrentFilters();
    if (saved) {
      if (saved.branch) setFBranch(saved.branch);
      if (saved.type) setFType(saved.type);
      if (saved.status) setFStatus(saved.status);
    }
  }, []);

  // Save current filters when they change
  useEffect(() => {
    saveCurrentFilters({ branch: fBranch, type: fType, status: fStatus });
  }, [fBranch, fType, fStatus, saveCurrentFilters]);

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data)).catch(() => {});
    loadSchedulerStatus();
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [fBranch, fType]);

  const loadSchedulerStatus = async () => {
    try {
      const { data } = await api.get('/admin/scheduler/status');
      setSchedulerStatus(data);
    } catch (err) { /* ignore if not admin */ }
  };

  const loadNotifications = async () => {
    try {
      const params = {};
      if (fBranch !== 'all') params.branch_id = fBranch;
      if (fType !== 'all') params.notification_type = fType;

      // Load system notifications (managers only)
      let sysNotifs = [];
      try {
        const { data } = await api.get('/notifications', { params });
        sysNotifs = data;
        const unreadIds = data.filter(n => !n.read).map(n => n.id);
        if (unreadIds.length > 0) {
          api.post('/notifications/mark-all-read').catch(() => {});
        }
      } catch { /* non-manager roles won't have access */ }

      // Load user workflow notifications (all roles)
      let userNotifs = [];
      try {
        const { data: uData } = await api.get('/user-notifications');
        userNotifs = uData.map(n => ({
          ...n,
          type: n.type || 'workflow',
          payload_json: JSON.stringify({ title: n.title, message: n.message }),
          created_at: n.created_at,
          _isUserNotif: true,
        }));
        if (userNotifs.some(n => !n.read)) {
          api.post('/user-notifications/mark-all-read').catch(() => {});
        }
      } catch { /* ignore */ }

      setNotifications([...userNotifs, ...sysNotifs]);
      if (window.__refreshTopBarCounts) window.__refreshTopBarCounts();
    } catch (err) {
      toast.error(t('toast.error'));
    } finally {
      setLoading(false);
    }
  };

  // Filtered and sorted notifications
  const filteredNotifications = useMemo(() => {
    let result = notifications;
    if (fStatus !== 'all') {
      if (fStatus === 'sent') {
        result = result.filter(n => n.delivered_at);
      } else if (fStatus === 'pending') {
        result = result.filter(n => !n.delivered_at);
      }
    }
    return sortData(result);
  }, [notifications, fStatus, sortData]);

  const triggerDailySummary = async () => {
    setGenerating('daily');
    try {
      const { data } = await api.post('/notifications/daily-summary');
      toast.success(data.message);
      loadNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setGenerating('');
    }
  };

  const triggerOverdueScan = async () => {
    setGenerating('overdue');
    try {
      const { data } = await api.post('/notifications/overdue-scan');
      toast.success(data.message);
      loadNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setGenerating('');
    }
  };

  const parsePayload = (jsonStr) => {
    try {
      return JSON.parse(jsonStr);
    } catch {
      return null;
    }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || id;

  const deliverNotification = async (notifId) => {
    setDelivering(notifId);
    try {
      const { data } = await api.post(`/delivery/deliver/${notifId}`);
      const attempts = data.attempts || [];
      setDeliveryAttempts(prev => ({ ...prev, [notifId]: attempts }));
      const sent = attempts.filter(a => a.status === 'sent').length;
      toast.success(`${t('notifications.delivered')}: ${sent}/${attempts.length} ${t('notifications.channel').toLowerCase()}s`);
      loadNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally { setDelivering(''); }
  };

  const loadAttempts = async (notifId) => {
    try {
      const { data } = await api.get('/delivery/attempts', { params: { notification_id: notifId } });
      setDeliveryAttempts(prev => ({ ...prev, [notifId]: data }));
    } catch (err) { /* ignore */ }
  };

  const clearFilters = () => {
    setFBranch('all');
    setFType('all');
    setFStatus('all');
  };

  const applyFilterPreset = (filters) => {
    if (filters.branch) setFBranch(filters.branch);
    if (filters.type) setFType(filters.type);
    if (filters.status) setFStatus(filters.status);
    toast.success(t('filters.applied'));
  };

  const hasActiveFilters = fBranch !== 'all' || fType !== 'all' || fStatus !== 'all';

  return (
    <div data-testid="notifications-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">
            {t('notifications.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {filteredNotifications.length} {t('notifications.title').toLowerCase()}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SavedFiltersDropdown
            savedFilters={savedFilters}
            onSave={saveFilterPreset}
            onApply={applyFilterPreset}
            onDelete={deleteFilterPreset}
            currentFilters={{ branch: fBranch, type: fType, status: fStatus }}
            t={t}
          />
          <Button
            size="sm"
            className="bg-brand hover:bg-brand-hover text-brand-foreground"
            onClick={triggerDailySummary}
            disabled={!!generating}
            data-testid="trigger-daily-summary-btn"
          >
            <BarChart3 className="w-3.5 h-3.5 me-1.5" />
            {generating === 'daily' ? t('common.loading') : t('notifications.dailySummary')}
          </Button>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={triggerOverdueScan}
            disabled={!!generating}
            data-testid="trigger-overdue-scan-btn"
          >
            <AlertTriangle className="w-3.5 h-3.5 me-1.5" />
            {generating === 'overdue' ? t('common.loading') : t('notifications.overdueScan')}
          </Button>
        </div>
      </div>

      {/* Scheduler Status */}
      {schedulerStatus && (
        <Card className="bg-card border-border shadow-sm" data-testid="scheduler-status-card">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start gap-3 mb-3 sm:mb-0">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${schedulerStatus.running ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t('notifications.schedulerStatus')} {schedulerStatus.running ? t('notifications.running') : t('notifications.stopped')}
                </p>
                <p className="text-xs text-muted-foreground">{t('notifications.dailySummary')} @ 09:00 AST, {t('notifications.overdueScan')} / 2h</p>
              </div>
            </div>
            {schedulerStatus.jobs?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-border">
                {schedulerStatus.jobs.map(job => (
                  <div key={job.id} className="text-start sm:text-end">
                    <p className="text-xs font-medium text-muted-foreground truncate">{job.name?.split('(')[0]?.trim()}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {t('notifications.nextRun')}: {job.next_run ? formatDate(job.next_run) : 'N/A'}
                    </p>
                    {job.last_run && (
                      <p className={`text-[11px] truncate ${job.last_run.status === 'success' ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {t('notifications.lastRun')}: {job.last_run.status}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={fBranch} onValueChange={setFBranch}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="notif-branch-filter">
            <SelectValue placeholder={t('common.all') + ' ' + t('common.branches')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="notif-type-filter">
            <SelectValue placeholder={t('common.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="daily_summary">{t('notifications.dailySummary')}</SelectItem>
            <SelectItem value="overdue_alert">{t('notifications.overdueScan')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="notif-status-filter">
            <SelectValue placeholder={t('common.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="pending">{t('notifications.pending')}</SelectItem>
            <SelectItem value="sent">{t('notifications.delivered')}</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
            <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
          </Button>
        )}
      </div>

      {/* Notifications Table */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <EnhancedTable stickyHeader>
            <StickyTableHeader>
              <tr className="border-b border-border">
                <SortableHeader field="type" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.type')}
                </SortableHeader>
                <SortableHeader field="branch_id" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.branch')}
                </SortableHeader>
                <SortableHeader field="created_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.createdAt')}
                </SortableHeader>
                <TableHeader>{t('common.status')}</TableHeader>
                <TableHeader>{t('notifications.details')}</TableHeader>
                <TableHeader align="end">{t('common.actions')}</TableHeader>
              </tr>
            </StickyTableHeader>
            <EnhancedTableBody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">{t('common.loading')}</td></tr>
              ) : filteredNotifications.length === 0 ? (
                <TableEmptyState icon={Bell} title={t('notifications.noNotifications')} description={t('notifications.triggerNow')} colSpan={6} />
              ) : (
                filteredNotifications.map((n) => {
                  const payload = parsePayload(n.payload_json);
                  const isDailySummary = n.type === 'daily_summary';

                  return (
                    <EnhancedTableRow key={n.id} data-testid={`notification-row-${n.id}`}>
                      <EnhancedTableCell>
                        <Badge 
                          variant="outline"
                          className={isDailySummary 
                            ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800' 
                            : 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                          }
                        >
                          {isDailySummary ? <BarChart3 className="w-3 h-3 me-1" /> : <AlertTriangle className="w-3 h-3 me-1" />}
                          {isDailySummary ? t('notifications.dailySummary') : t('notifications.overdueScan')}
                        </Badge>
                      </EnhancedTableCell>
                      <EnhancedTableCell className="text-muted-foreground">{branchName(n.branch_id)}</EnhancedTableCell>
                      <EnhancedTableCell className="text-xs text-muted-foreground">{formatDate(n.created_at)}</EnhancedTableCell>
                      <EnhancedTableCell>
                        {n.delivered_at ? (
                          <Badge variant="default" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                            <CheckCircle className="w-3 h-3 me-1" /> {t('notifications.delivered')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Clock className="w-3 h-3 me-1" /> {t('notifications.pending')}
                          </Badge>
                        )}
                      </EnhancedTableCell>
                      <EnhancedTableCell>
                        {payload && isDailySummary && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground">{t('status.OPEN')}: <strong className="text-foreground">{payload.open_wos}</strong></span>
                            <span className="text-rose-600">{t('dashboard.overdueOrders')}: <strong>{payload.overdue_wos}</strong></span>
                          </div>
                        )}
                        {payload && !isDailySummary && (
                          <div className="text-xs text-muted-foreground">
                            WO <span className="font-mono">{payload.wo_id_short}</span> - {payload.days_overdue} {t('common.days')}
                          </div>
                        )}
                      </EnhancedTableCell>
                      <EnhancedTableCell align="end">
                        {!n.delivered_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            onClick={() => deliverNotification(n.id)}
                            disabled={delivering === n.id}
                            data-testid={`deliver-btn-${n.id}`}
                          >
                            <Send className="w-3 h-3 me-1" />
                            {delivering === n.id ? t('common.loading') : t('notifications.deliverNow')}
                          </Button>
                        )}
                        {n.delivered_at && !deliveryAttempts[n.id] && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-muted-foreground"
                            onClick={() => loadAttempts(n.id)}
                            data-testid={`load-attempts-${n.id}`}
                          >
                            {t('notifications.deliveryAttempts')}
                          </Button>
                        )}
                      </EnhancedTableCell>
                    </EnhancedTableRow>
                  );
                })
              )}
            </EnhancedTableBody>
          </EnhancedTable>
        </CardContent>
      </Card>

      {/* Delivery Attempts Expandable Section */}
      {Object.keys(deliveryAttempts).length > 0 && (
        <Card className="bg-card border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-barlow">{t('notifications.deliveryAttempts')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(deliveryAttempts).map(([notifId, attempts]) => (
              <div key={notifId} className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2">Notification: {notifId.slice(0, 8)}...</p>
                <div className="space-y-1">
                  {attempts.map(att => (
                    <div key={att.id} className="flex items-center gap-2 text-xs text-muted-foreground py-0.5">
                      {att.status === 'sent' ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : att.status === 'failed' ? <XCircle className="w-3 h-3 text-rose-500" /> : <Clock className="w-3 h-3" />}
                      <span className="capitalize font-medium">{att.channel_type}</span>
                      <span className="text-muted-foreground">{att.destination}</span>
                      <span className={att.status === 'sent' ? 'text-emerald-500' : att.status === 'failed' ? 'text-rose-500' : ''}>
                        {att.status === 'sent' ? t('notifications.delivered') : att.status === 'failed' ? t('notifications.failed') : t('notifications.pending')}
                      </span>
                      {att.error_text && <span className="text-rose-400 truncate max-w-[200px]">{att.error_text}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
